import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { dedupInvoicesById } from "@/lib/customer-detail-calc";
import { DBProject, DBInvoice, DBRecurringAgreement } from "@/lib/db-types";

export interface CustomerDetail {
  id: string;
  name: string;
  organization_number: string | null;
  contact_person: string | null;
  contact_email: string | null;
  /** VIB-2521: PO `InvoiceEmailAddress` — faktisk fakturamottaker. */
  invoice_email_address: string | null;
  invoice_email_cc: string | null;
  contact_phone: string | null;
  address: string | null;
  postal_code: string | null;
  city: string | null;
  poweroffice_customer_id: number | null;
  country_code: string;
  /** Selskap (ikke privatperson); Brønnøysund-synken henter bare selskaper. */
  is_company: boolean | null;
  /** Kundekort v3: logo hentet fra kundens nettside av brreg-sync-customers, eller lastet opp. */
  logo_url: string | null;
}

export interface CustomerDetailWithProjects {
  customer: CustomerDetail | null;
  projects: DBProject[];
  invoices: DBInvoice[];
  agreements: DBRecurringAgreement[];
  isLoading: boolean;
  error: Error | null;
}

export function useCustomerDetail(customerId: string | undefined) {
  return useQuery({
    queryKey: ["customer-detail", customerId],
    queryFn: async (): Promise<CustomerDetailWithProjects> => {
      if (!customerId) {
        return { customer: null, projects: [], invoices: [], agreements: [], isLoading: false, error: null };
      }

      // Pass 1: kunde, prosjekter og avtaler i parallell. Fakturaene henter vi
      // etterpå fordi de er bundet på prosjekt-/avtale-IDene herfra.
      const [customerRes, projectsRes, agreementsRes] = await Promise.all([
        supabase
          .from("customers")
          .select(
            "id, name, organization_number, contact_person, contact_email, invoice_email_address, invoice_email_cc, contact_phone, address, postal_code, city, poweroffice_customer_id, country_code, is_company, logo_url"
          )
          .eq("id", customerId)
          .single(),
        supabase
          .from("projects")
          .select("*")
          .eq("customer_id", customerId)
          .is("deleted_at", null)
          .order("name"),
        supabase
          .from("recurring_agreements")
          .select("*")
          .eq("customer_id", customerId)
          .is("deleted_at", null)
          .order("next_run_date", { ascending: true }),
      ]);

      if (customerRes.error) throw customerRes.error;
      if (projectsRes.error) throw projectsRes.error;
      if (agreementsRes.error) throw agreementsRes.error;

      const projects = (projectsRes.data ?? []) as DBProject[];
      const agreements = (agreementsRes.data ?? []) as unknown as DBRecurringAgreement[];
      const projectIds = projects.map((p) => p.id);
      const agreementIds = agreements.map((a) => a.id);

      // Pass 2: fakturaer via kundens prosjekter OG via kundens avtaler. Begge
      // spørringene er bundet på IN-lister (ingen ubundet team-bred lesing),
      // så paginering er ikke nødvendig her. Dedup på id (en avtale-faktura kan
      // også være prosjekt-koblet).
      // VIB-3030: en faktura kan gå til en annen kunde enn prosjektets. Den
      // teller hos kunden som ble fakturert: hentes via invoices.customer_id,
      // og tas ut av hovedkundens prosjektfakturaer.
      const [byProjectAll, byAgreement, byCustomer] = await Promise.all([
        projectIds.length
          ? supabase
              .from("invoices")
              .select("*")
              .in("project_id", projectIds)
              .then((res) => {
                if (res.error) throw res.error;
                return (res.data ?? []) as DBInvoice[];
              })
          : Promise.resolve([] as DBInvoice[]),
        agreementIds.length
          ? supabase
              .from("invoices")
              .select("*")
              .in("recurring_agreement_id", agreementIds)
              .then((res) => {
                if (res.error) throw res.error;
                return (res.data ?? []) as DBInvoice[];
              })
          : Promise.resolve([] as DBInvoice[]),
        supabase
          .from("invoices")
          .select("*")
          .eq("customer_id", customerId)
          .then((res) => {
            if (res.error) throw res.error;
            return (res.data ?? []) as DBInvoice[];
          }),
      ]);
      const byProject = byProjectAll.filter((inv) => !inv.customer_id || inv.customer_id === customerId);

      const invoices = dedupInvoicesById(byProject, byAgreement, byCustomer).sort((a, b) =>
        (b.invoice_date ?? b.created_at ?? "").localeCompare(a.invoice_date ?? a.created_at ?? ""),
      );

      return {
        customer: customerRes.data ?? null,
        projects,
        invoices,
        agreements,
        isLoading: false,
        error: null,
      };
    },
    enabled: !!customerId,
    staleTime: 5 * 60 * 1000,
  });
}
